import type { PostingExprNode, PostingNode } from '@revelio/data-access';

import { DEFAULT_ACTIVITY_TAXONOMY_ID } from '../../../filters/contexts/defaultFilters';
import type { CustomActivityTaxonomySelection } from '../../../filters/definitions/activityTaxonomy';
import { findFilterById } from '../../../filters/engine/filterRegistry';
import {
  type Filter,
  FilterId,
  FilterType,
  isMultiFilter,
  type SingleValueFilter,
  type TreeFilter,
} from '../../../filters/types/filters';
import { getActivityLevelForLevelId } from '../../../filters/utils/role-skill-ent-kind';
import {
  buildActivityValNode,
  buildTreeFilterNodes,
  type RefData,
  wrapExprNode,
} from './node-builders';

export const getActivityTaxonomyId = (
  filters: Filter[]
): number | undefined => {
  const activityTaxonomyFilter = findFilterById(
    filters,
    FilterId.ACTIVITY_TAXONOMY
  );
  if (
    !activityTaxonomyFilter ||
    activityTaxonomyFilter.type !== FilterType.VALUE
  ) {
    return undefined;
  }

  const value = (
    activityTaxonomyFilter as SingleValueFilter<CustomActivityTaxonomySelection>
  ).value;
  if (value && typeof value.taxonomyId === 'number') {
    return value.taxonomyId;
  }
  return DEFAULT_ACTIVITY_TAXONOMY_ID;
};

const makeActivityLeafFactory =
  (taxonomyId: number) =>
  (id: number, levelId: string): PostingExprNode | null => {
    const level = getActivityLevelForLevelId(levelId);
    if (level === null) return null;
    const ref: RefData = { level, taxonomyId };
    return buildActivityValNode(id, ref);
  };

export const buildActivityFilter = (filters: Filter[]): PostingNode | null => {
  const activityFilter = findFilterById(filters, FilterId.ACTIVITY);
  if (!activityFilter || activityFilter.type !== FilterType.TREE) {
    return null;
  }

  const makeLeaf = makeActivityLeafFactory(
    getActivityTaxonomyId(filters) ?? DEFAULT_ACTIVITY_TAXONOMY_ID
  );
  const exprNode = buildTreeFilterNodes(activityFilter, makeLeaf);
  if (!exprNode) return null;

  return wrapExprNode(exprNode, activityFilter.isNot);
};

/**
 * MULTI_ACTIVITY twin of {@link buildActivityFilter}: each instance is its own
 * OR group and the instances are AND-ed. Mirrors `buildMultiCustomSkillFilter`.
 */
export const buildMultiActivityFilter = (
  filters: Filter[]
): PostingNode | PostingNode[] | null => {
  const multiActivity = findFilterById(filters, FilterId.MULTI_ACTIVITY);
  if (!multiActivity || !isMultiFilter(multiActivity)) {
    return null;
  }
  if (multiActivity.instances.length === 0) {
    return null;
  }

  const makeLeaf = makeActivityLeafFactory(
    getActivityTaxonomyId(filters) ?? DEFAULT_ACTIVITY_TAXONOMY_ID
  );

  const instanceNodes = multiActivity.instances
    .filter((inst): inst is TreeFilter => inst?.type === FilterType.TREE)
    .map((instance) => {
      const exprNode = buildTreeFilterNodes(instance, makeLeaf);
      if (!exprNode) return null;
      return wrapExprNode(exprNode, instance.isNot);
    })
    .filter((node): node is PostingNode => node !== null);

  if (instanceNodes.length === 0) return null;
  if (instanceNodes.length === 1) {
    const [singleNode] = instanceNodes;
    return singleNode ?? null;
  }
  return instanceNodes;
};
